import { Component, computed, effect, inject, OnInit, signal } from '@angular/core';
import { BusinessStore } from 'src/model/business/business-store';
import { FiscalYearInfo } from 'src/model/fiscalYearInfo.model';
import { FiscalYearInfoService } from 'src/services/fiscal-year-info.service';
import { AddUpdateFiscalYearInfoComponent } from '../add-update-fiscal-year-info/add-update-fiscal-year-info.component';
import { MatTableController } from '../mat-table-controller';
import { formatMonth, toAbsoluteMonth } from '../month.util';
import { Enums } from '../Shared/enums';
import { SharedService } from '../Shared/shared-service.service';

interface FiscalYearInfoGroup {
  year: number;
  expanded?: boolean;
  items: FiscalYearInfo[];
}

@Component({
  selector: 'app-fiscal-year-info-list',
  templateUrl: './fiscal-year-info-list.component.html',
  styleUrl: './fiscal-year-info-list.component.scss',
  standalone: false,
  providers: [MatTableController]
})
export class FiscalYearInfoListComponent implements OnInit {
  private fyInfoService = inject(FiscalYearInfoService);
  private businessStore = inject(BusinessStore);
  protected sharedService = inject(SharedService);
  protected enums = inject(Enums);
  protected table = inject(MatTableController<FiscalYearInfo>);

  businessList = this.businessStore.businessList;
  branchMap = this.businessStore.branchMap;
  branchBusinessMap = this.businessStore.branchBusinessMap;

  searchText = signal<string>('');
  fyInfoList = signal<FiscalYearInfo[]>([]);

  // businessYearEnd = computed<Map<number, number>>(() => {
  //   const biz = this.businessList();
  //   return biz ? new Map(biz.map((b) => [b.Id, b.YearEndMonth])) : undefined;
  // });
  displayedColumns = computed(() => [...this.table.columnDefs(), 'Actions']);
  currentYear = new Date().getFullYear();
  today = new Date();
  absCurrentMonth = toAbsoluteMonth(this.today.getFullYear(), this.today.getMonth());
  fyInfoMap = computed<Map<number, FiscalYearInfo[]>>(() => {
    const list = [...this.fyInfoList()].sort((a, b) => b.FiscalYear - a.FiscalYear);
    return this.sharedService.groupByNumKeyAsMap(list, 'FiscalYear');
  });
  fiscalYears = computed<number[]>(()  => Array.from(this.fyInfoMap().keys()).slice(0, 3));
  yearFilter = signal<number[]>([]);
  fyInfoGroup = computed<FiscalYearInfoGroup[]>(() => {
    const map = this.fyInfoMap();
    const filter = this.yearFilter();
    const years = filter.length > 0 ? filter : null;
    const result: FiscalYearInfoGroup[] = [];
    for (const [key, items] of map.entries()) {
      if (years && !years.includes(key)) continue;
      if (items?.length > 0) {
        result.push({
          year: Number(key),
          items: items.map(f => ({
            ...f,
            YearEnd: this.branchBusinessMap()?.get(f.BranchId)?.YearEndMonth,
            EndMonth: toAbsoluteMonth(f.FiscalYear, this.branchBusinessMap()?.get(f.BranchId)?.YearEndMonth)
          })).sort((a, b) => b.YearEnd - a.YearEnd)
        })
      }
    }
    return result;
  });

  constructor() {
    this.table.hideGroupHeader.set(true);
    this.table.config.set([
      { columnDef: 'Business', header: 'Business', calculated: (f) => this.branchBusinessMap()?.get(f.BranchId)?.Name },
      { columnDef: 'Branch', header: 'Branch / City', calculated: (f) => this.branchMap()?.get(f.BranchId)?.Name.length > 0 ? this.branchMap()?.get(f.BranchId)?.Name : this.branchMap()?.get(f.BranchId)?.Address.City },
      { columnDef: 'FiscalYear', header: 'Fiscal Year', property: 'FiscalYear' },
      { columnDef: 'YearEnd', header: 'Year End', format: v => formatMonth(v, 'long'), filterBy: 'formatted' },
      { columnDef: 'Amount', header: 'Amount', property: 'Amount', type: 'currency', cssClass: { header: 'align-end', cell: 'text-end' } },
      { columnDef: 'CfAdjustment', header: 'CF Adjustment', property: 'CfAdjustment', type: 'currency', cssClass: { header: 'align-end', cell: 'text-end' } }
    ]);

    effect(() => {
      const g = this.fyInfoGroup();
      this.table.rawData.set([...g.map(d => ({ headerRow: d, items: d.items }))]);
    })

    effect(() => {
      this.table.filterQuery.set(this.searchText() ?? '');
    })
  }

  ngOnInit(): void {
    this.getAllFiscalYearInfo();
  }

  async getAllFiscalYearInfo() {
    const f = await this.fyInfoService.getFiscalYearInfos();
    this.fyInfoList.set(f);
  }

  addFiscalYearInfo() {
    const modalRef = this.sharedService.openModal(AddUpdateFiscalYearInfoComponent, 'regularModal');
    const instance = modalRef.componentInstance as AddUpdateFiscalYearInfoComponent;
    instance.passEntry.subscribe((f: FiscalYearInfo) => {
      this.fyInfoList.update(list => [...list, f]);
    });
  }

  updateFiscalYearInfo(fyInfo: FiscalYearInfo) {
    const modalRef = this.sharedService.openModal(AddUpdateFiscalYearInfoComponent, 'regularModal');
    const instance = modalRef.componentInstance as AddUpdateFiscalYearInfoComponent;
    instance.isEdit = true;
    instance.lockBusiness = true;
    instance.fyInfoToUpdate = fyInfo;
    instance.passEntry.subscribe((f: FiscalYearInfo) => {
      this.fyInfoList.update(list => {
        const i = list.findIndex(l => l.BranchId === f.BranchId && l.FiscalYear === f.FiscalYear);
        list.splice(i, 1, f);
        return [...list];
      });
    })
  }

  setFilterYear(year: number) {
    this.yearFilter.update(filter => {
      const i = filter.indexOf(year);
      i > -1 ? filter.splice(i, 1) : filter.push(year);
      return [...filter];
    })
  }

  isHeader(index, item): boolean {
    return item.hasOwnProperty('year');
  }
}
